import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import NumInput from "@/components/NumInput";
import { Switch } from "@/components/ui/switch";

const GROUPS = [["superadmin", "Superadmin"], ["staff", "Staff"], ["customer", "Customer"]];

function RoleGroup({ role, label, rows, me, setRole }) {
  const [q, setQ] = useState("");
  const k = q.trim().toLowerCase();
  const list = rows.filter((u) => !k || [u.name, u.email, u.phone].some((v) => (v || "").toLowerCase().includes(k)));
  return (
    <section className="space-y-3" data-testid={`users-group-${role}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{label.toUpperCase()} <span className="text-[#6E6259]" data-testid={`users-count-${role}`}>({rows.length})</span></h2>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cari ${label}...`} className={`${inputCls} sm:w-64`} data-testid={`users-search-${role}`} />
      </div>
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {list.map((u) => (
          <div key={u.user_id} className="p-4 flex flex-wrap items-center justify-between gap-4 text-sm" data-testid={`user-row-${u.email}`}>
            <div className="min-w-0"><p>{u.name}</p><p className="text-xs text-[#6E6259] break-all">{u.email}{u.phone ? ` · ${u.phone}` : ""} · {u.marketing_opt_in ? "subscribed" : "not subscribed"}</p></div>
            <select disabled={u.user_id === me.user_id} value={u.role || "customer"} onChange={(e) => setRole(u, e.target.value)} className="w-40 border border-[#E6DFD5] bg-white px-3 py-2 text-sm outline-none focus:border-[#8C4F27]" data-testid={`role-select-${u.email}`}>
              {GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        ))}
        {!list.length && <p className="p-4 text-sm text-[#6E6259]">—</p>}
      </div>
    </section>
  );
}

export function UsersAdmin() {
  const { user } = useApp();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (u, role) => { try { await api.patch(`/admin/users/${u.user_id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="admin-users" className="space-y-10">
      <AdminTitle>Users & Roles</AdminTitle>
      <p className="text-sm text-[#6E6259] -mt-6">New accounts start as Customer. Changing the role moves the person to the matching group. Staff can only manage inventory, orders and bookings.</p>
      {GROUPS.map(([role, label]) => (
        <RoleGroup key={role} role={role} label={label} me={user} setRole={setRole} rows={rows.filter((u) => (u.role || "customer") === role)} />
      ))}
    </div>
  );
}

export function SettingsAdmin() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const n = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const save = async () => { try { const r = await api.put("/admin/settings", f); setF(r.data); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-2xl" data-testid="admin-settings">
      <AdminTitle>Settings</AdminTitle>
      <div className="bg-white border border-[#E6DFD5] p-6 space-y-4">
        <Field label="Welcome discount for new accounts (%)"><NumInput value={f.welcome_discount_pct} onChange={n("welcome_discount_pct")} className={inputCls} data-testid="settings-welcome-pct" /></Field>
        <Field label="Member discount for signed-in buyers, every order (%)"><NumInput value={f.member_discount_pct} onChange={n("member_discount_pct")} className={inputCls} data-testid="settings-member-pct" /></Field>
        <Field label="Cart reminder: send after the cart is left for (hours)"><NumInput value={f.cart_reminder_hours} onChange={n("cart_reminder_hours")} className={inputCls} data-testid="settings-cart-hours" /></Field>
        <Field label="USD exchange rate (IDR per 1 USD) — used for the currency switcher"><NumInput value={f.usd_rate} onChange={n("usd_rate")} className={inputCls} data-testid="settings-usd-rate" /></Field>
        <Field label="Store / pickup address"><textarea rows={2} value={f.store_address} onChange={s("store_address")} className={inputCls} data-testid="settings-store-address" /></Field>
        <Field label="Origin postal code (for courier rates)"><input value={f.origin_postal_code} onChange={s("origin_postal_code")} className={inputCls} data-testid="settings-origin-postal" /></Field>
        <Field label="WhatsApp number (international format, e.g. 62812...)"><input value={f.whatsapp_number} onChange={s("whatsapp_number")} className={inputCls} data-testid="settings-whatsapp" /></Field>
        <Field label="Contact email"><input value={f.contact_email} onChange={s("contact_email")} className={inputCls} /></Field>
        <Btn onClick={save} data-testid="settings-save-btn">Save settings</Btn>
      </div>
    </div>
  );
}
